/**
 * The editor's keyboard map: one table of actions and their default keys, matching of key events (Mac and
 * Windows/Linux modifiers), the user's rebindings (kept per browser) and conflict checks. Pure: no React, no DOM
 * beyond reading a KeyboardEvent's fields, so it is unit tested with node:test.
 *
 * Conventions (see the PR for the research behind them):
 * - Single letters switch tools, as in Figma, SketchUp and Blender (V select, P panel, X obstacle, M measure,
 *   H hand/pan, C comment, E roof edges); hold Space to pan with any tool (Figma, Photoshop, most map editors).
 * - `mod` is ⌘ on a Mac and Ctrl elsewhere, for the commands people already know (undo, redo, save, select all,
 *   duplicate). Alt is never needed for a common action: on a Mac it changes the typed character.
 * - S, Shift+O, Shift+S and B open the side panel's plan tabs (Savings, Options, Setbacks, Bill of materials), Shift+K
 *   the shade report. The analysis layers use I, A, K, Shift+H/C/I and U (sun & shade; Shift+U plays the sun).
 * - Every default combo is used once in the whole table, whatever the scope (hotkeys.test.ts fails otherwise).
 * - Shift+D, Shift+E and Shift+P switch between the three views of a design, Design | Sketch | Permit
 *   (editor/workspace.ts; the same keys in the Sketch tools); Shift+P in the Permit workspace goes back to Design. P
 *   alone adds panels, so the permit plan tab moved to Alt+P. The workspace's own keys have the scope "permit" and only work there: Alt+1…5
 *   the steps, N / Shift+N the next / previous problem (the editor's [ ] are its months), Shift+L locate,
 *   Shift+F fix it, \ the checks panel, PgUp/PgDn the sheets, F2 rename a diagram element, ⌘/Ctrl+Shift+G the
 *   generate card (it never charges in one keystroke).
 * - Number keys pick the view (1 2D, 2 3D, 3 presentation), like an RTS's camera bookmarks; F frames the selection
 *   or the house (Maya, Unity, Blender's numpad "." / Home).
 * - Esc unwinds one layer at a time: the drawing in progress, then the selection, then an open overlay.
 * - Nothing fires while typing in an input, a textarea, a select or a contenteditable, except a binding marked
 *   `inInputs` (⌘/Ctrl+S, so saving never opens the browser's "Save page" dialog).
 * - The Sketch's wheel gestures (scope "sketch") are combos ending in `wheel`: a modifier held while turning the wheel
 *   over the map (Shift: size, or turn the selected panels; Alt: shape or kind, or portrait ⇄ landscape;
 *   Shift+Alt: height). The wheel alone zooms; Ctrl/⌘ + wheel is left to the browser's zoom and to pinching on a
 *   trackpad (which sends it), so it is never a default. They are rebindable like keys (turn the wheel with the new
 *   modifiers).
 */

import type { MessageKey } from "../i18n/keys";

export type Scope = "global" | "2d" | "3d" | "permit" | "sketch";
export type Group = "Tools" | "Edit" | "Selection" | "View" | "Side panel" | "Layers" | "Permit" | "Sketch" | "Help";

export interface ActionDef {
  id: string;
  group: Group;
  /** Default keys, e.g. "mod+shift+z"; several alternatives allowed. */
  keys: string[];
  /** Where the action applies; "global" bindings conflict with every scope. */
  scope: Scope;
  /** Fires also while an input has the focus. */
  inInputs?: boolean;
  /** Fires again while the key is held (nudges); others ignore auto-repeat. */
  repeat?: boolean;
  /** Shown in the cheat sheet instead of a key (pointer gestures): a catalog key. */
  gesture?: MessageKey;
  /** Actions sharing a row in the cheat sheet (the four nudge directions); the row shows the first's label. */
  sheetRow?: string;
}

export const ACTIONS: ActionDef[] = [
  // tools (2D)
  { id: "tool.select", group: "Tools", keys: ["v"], scope: "2d" },
  { id: "tool.add", group: "Tools", keys: ["p"], scope: "2d" },
  { id: "tool.polygon", group: "Tools", keys: ["x"], scope: "2d" },
  { id: "tool.circle", group: "Tools", keys: ["shift+x"], scope: "2d" },
  { id: "tool.measure", group: "Tools", keys: ["m"], scope: "2d" },
  { id: "tool.pan", group: "Tools", keys: ["h"], scope: "2d" },
  { id: "tool.roof", group: "Tools", keys: ["e"], scope: "2d" },
  { id: "tool.comment", group: "Tools", keys: ["c"], scope: "global" },
  { id: "tool.section", group: "Tools", keys: ["t"], scope: "2d" },
  // edit
  { id: "edit.undo", group: "Edit", keys: ["mod+z"], scope: "global", repeat: true },
  { id: "edit.redo", group: "Edit", keys: ["mod+shift+z", "mod+y"], scope: "global", repeat: true },
  { id: "edit.save", group: "Edit", keys: ["mod+s"], scope: "global", inInputs: true },
  { id: "edit.delete", group: "Edit", keys: ["delete", "backspace"], scope: "global" },
  { id: "edit.duplicate", group: "Edit", keys: ["mod+d"], scope: "global" },
  { id: "edit.rotate", group: "Edit", keys: ["r"], scope: "global", repeat: true },
  { id: "edit.rotateBack", group: "Edit", keys: ["alt+r"], scope: "global", repeat: true },
  { id: "edit.rotate90", group: "Edit", keys: ["shift+r"], scope: "global" },
  { id: "edit.orientation", group: "Edit", keys: ["o"], scope: "global" },
  { id: "edit.snap", group: "Edit", keys: ["g"], scope: "2d" },
  { id: "draw.finish", group: "Edit", keys: ["enter"], scope: "2d" },
  { id: "nudge.left", group: "Edit", keys: ["arrowleft"], scope: "2d", repeat: true, sheetRow: "nudge" },
  { id: "nudge.right", group: "Edit", keys: ["arrowright"], scope: "2d", repeat: true, sheetRow: "nudge" },
  { id: "nudge.up", group: "Edit", keys: ["arrowup"], scope: "2d", repeat: true, sheetRow: "nudge" },
  { id: "nudge.down", group: "Edit", keys: ["arrowdown"], scope: "2d", repeat: true, sheetRow: "nudge" },
  { id: "nudge.leftBig", group: "Edit", keys: ["shift+arrowleft"], scope: "2d", repeat: true, sheetRow: "nudgeBig" },
  { id: "nudge.rightBig", group: "Edit", keys: ["shift+arrowright"], scope: "2d", repeat: true, sheetRow: "nudgeBig" },
  { id: "nudge.upBig", group: "Edit", keys: ["shift+arrowup"], scope: "2d", repeat: true, sheetRow: "nudgeBig" },
  { id: "nudge.downBig", group: "Edit", keys: ["shift+arrowdown"], scope: "2d", repeat: true, sheetRow: "nudgeBig" },
  // selection
  { id: "select.all", group: "Selection", keys: ["mod+a"], scope: "global" },
  { id: "select.cancel", group: "Selection", keys: ["escape"], scope: "global" },
  { id: "select.box", group: "Selection", keys: [], scope: "2d", gesture: "editor.hotkeys.gesture.dragEmptyRoof" },
  { id: "select.add", group: "Selection", keys: [], scope: "global", gesture: "editor.hotkeys.gesture.shiftClick" },
  { id: "select.toggle", group: "Selection", keys: [], scope: "2d", gesture: "editor.hotkeys.gesture.modClick" },
  // view
  { id: "view.2d", group: "View", keys: ["1"], scope: "global" },
  { id: "view.3d", group: "View", keys: ["2"], scope: "global" },
  { id: "view.present", group: "View", keys: ["3"], scope: "global" },
  { id: "view.frame", group: "View", keys: ["f"], scope: "global" },
  { id: "view.layers", group: "View", keys: ["l"], scope: "global" },
  { id: "view.panHold", group: "View", keys: ["space"], scope: "2d", gesture: "editor.hotkeys.gesture.spaceDrag" },
  { id: "view.zoom", group: "View", keys: [], scope: "global", gesture: "editor.hotkeys.gesture.wheelPinch" },
  // side panel (plan tabs, and the shade report tab)
  { id: "panel.savings", group: "Side panel", keys: ["s"], scope: "global" },
  { id: "panel.options", group: "Side panel", keys: ["shift+o"], scope: "global" },
  { id: "panel.setbacks", group: "Side panel", keys: ["shift+s"], scope: "global" },
  { id: "panel.bom", group: "Side panel", keys: ["b"], scope: "global" },
  { id: "panel.permits", group: "Side panel", keys: ["alt+p"], scope: "global" },
  { id: "layer.report", group: "Side panel", keys: ["shift+k"], scope: "global" },
  // analysis layers (layers/): a key toggles its layer; the raster layers replace each other
  { id: "layer.height", group: "Layers", keys: ["shift+h"], scope: "global" },
  { id: "layer.contours", group: "Layers", keys: ["shift+c"], scope: "global" },
  { id: "layer.irradiance", group: "Layers", keys: ["i"], scope: "global" },
  { id: "layer.monthPrev", group: "Layers", keys: ["["], scope: "global", sheetRow: "month" },
  { id: "layer.monthNext", group: "Layers", keys: ["]"], scope: "global", sheetRow: "month" },
  { id: "layer.access", group: "Layers", keys: ["a"], scope: "global" },
  { id: "layer.tsrf", group: "Layers", keys: ["shift+i"], scope: "global" },
  { id: "layer.kwh", group: "Layers", keys: ["k"], scope: "global" },
  { id: "layer.sun", group: "Layers", keys: ["u"], scope: "global" },
  { id: "sun.play", group: "Layers", keys: ["shift+u"], scope: "global" },
  { id: "sun.earlier", group: "Layers", keys: [","], scope: "global", repeat: true, sheetRow: "sunTime" },
  { id: "sun.later", group: "Layers", keys: ["."], scope: "global", repeat: true, sheetRow: "sunTime" },
  { id: "sun.dayBack", group: "Layers", keys: ["<"], scope: "global", repeat: true, sheetRow: "sunDay" },
  { id: "sun.dayOn", group: "Layers", keys: [">"], scope: "global", repeat: true, sheetRow: "sunDay" },
  // the Design | Sketch | Permit switch (editor/workspace.ts): the same keys in the Designer, the Sketch tools and the
  // Permit workspace (Shift+P there goes back to Design)
  { id: "mode.design", group: "View", keys: ["shift+d"], scope: "global" },
  { id: "mode.sketch", group: "View", keys: ["shift+e"], scope: "global" },
  { id: "mode.permit", group: "View", keys: ["shift+p"], scope: "global" },
  // the Permit workspace
  { id: "permit.step1", group: "Permit", keys: ["alt+1"], scope: "permit", sheetRow: "steps" },
  { id: "permit.step2", group: "Permit", keys: ["alt+2"], scope: "permit", sheetRow: "steps" },
  { id: "permit.step3", group: "Permit", keys: ["alt+3"], scope: "permit", sheetRow: "steps" },
  { id: "permit.step4", group: "Permit", keys: ["alt+4"], scope: "permit", sheetRow: "steps" },
  { id: "permit.step5", group: "Permit", keys: ["alt+5"], scope: "permit", sheetRow: "steps" },
  { id: "permit.nextProblem", group: "Permit", keys: ["n"], scope: "permit" },
  { id: "permit.prevProblem", group: "Permit", keys: ["shift+n"], scope: "permit" },
  { id: "permit.locate", group: "Permit", keys: ["shift+l"], scope: "permit" },
  { id: "permit.fix", group: "Permit", keys: ["shift+f"], scope: "permit" },
  { id: "permit.checks", group: "Permit", keys: ["\\"], scope: "permit" },
  { id: "permit.sheetPrev", group: "Permit", keys: ["pageup"], scope: "permit", sheetRow: "sheets" },
  { id: "permit.sheetNext", group: "Permit", keys: ["pagedown"], scope: "permit", sheetRow: "sheets" },
  { id: "permit.rename", group: "Permit", keys: ["f2"], scope: "permit" },
  { id: "permit.resetElement", group: "Permit", keys: ["shift+backspace"], scope: "permit" },
  { id: "permit.generate", group: "Permit", keys: ["mod+shift+g"], scope: "permit" },
  // the Sketch (/designs/<id>/sketch): modifier + wheel over the map, on the selected or the next obstacle, tree or panels
  { id: "sketch.wheelSize", group: "Sketch", keys: ["shift+wheel"], scope: "sketch" },
  { id: "sketch.wheelKind", group: "Sketch", keys: ["alt+wheel"], scope: "sketch" },
  { id: "sketch.wheelHeight", group: "Sketch", keys: ["alt+shift+wheel"], scope: "sketch" },
  { id: "sketch.variants", group: "Sketch", keys: [], scope: "sketch", gesture: "editor.hotkeys.gesture.hoverTool" },
  // help
  { id: "help.sheet", group: "Help", keys: ["?"], scope: "global" },
];

/** The cheat sheet's two columns of about the same height (every group once). */
export const SHEET_COLUMNS: Group[][] = [["Tools", "Selection", "View", "Side panel", "Help"], ["Edit", "Layers"], ["Permit", "Sketch"]];

export const ACTION_BY_ID = new Map(ACTIONS.map((a) => [a.id, a]));

/** An action's label, as a catalog key (`editor.hotkeys.action.<id>`; the cheat sheet resolves it with t()). */
// a key segment may not start with a digit (view.2d → view.n2d)
export const actionLabelKey = (id: string) => `editor.hotkeys.action.${id.replace(/\.(\d)/g, ".n$1")}` as MessageKey;

const GROUP_KEYS: Record<Group, MessageKey> = {
  Tools: "editor.hotkeys.group.tools",
  Edit: "editor.hotkeys.group.edit",
  Selection: "editor.hotkeys.group.selection",
  View: "editor.hotkeys.group.view",
  "Side panel": "editor.hotkeys.group.sidePanel",
  Layers: "editor.hotkeys.group.layers",
  Permit: "editor.hotkeys.group.permit",
  Sketch: "editor.hotkeys.group.sketch",
  Help: "editor.hotkeys.group.help",
};
/** A group's heading, as a catalog key. */
export const groupLabelKey = (group: Group) => GROUP_KEYS[group];

// ------------------------------------------------------------------------------------------------ combos
const MOD_ORDER = ["mod", "ctrl", "alt", "shift"] as const;
const NAMED: Record<string, string> = {
  " ": "space",
  spacebar: "space",
  esc: "escape",
  del: "delete",
  left: "arrowleft",
  right: "arrowright",
  up: "arrowup",
  down: "arrowdown",
};

/** "Shift+Mod+Z" → "mod+shift+z": lower case, modifiers in a fixed order. */
export function normalizeCombo(combo: string): string {
  const parts = combo.toLowerCase().split("+").map((p) => p.trim());
  // "mod++" (the plus key) splits into empty parts
  const key = combo.endsWith("++") || combo === "+" ? "+" : parts.pop() || "";
  const mods = new Set(parts.filter(Boolean).map((m) => (m === "cmd" || m === "meta" || m === "command" ? "mod" : m === "control" ? "ctrl" : m === "option" ? "alt" : m)));
  return [...MOD_ORDER.filter((m) => mods.has(m)), NAMED[key] ?? key].join("+");
}

export interface KeyLike {
  key: string;
  code?: string;
  metaKey: boolean;
  ctrlKey: boolean;
  altKey: boolean;
  shiftKey: boolean;
}

/** The combo a key event stands for. On a Mac ⌘ is `mod` (Ctrl stays `ctrl`); elsewhere Ctrl is `mod`.
 * Letters and digits come from the physical key when Alt changed the character (Mac Option+R types "®") or the
 * layout is not Latin; a shifted symbol ("?") is its own key, so Shift is not part of it. */
export function eventCombo(e: KeyLike, mac: boolean): string {
  let key = NAMED[e.key.toLowerCase()] ?? e.key.toLowerCase();
  const code = e.code ?? "";
  const latin = /^[a-z0-9]$/.test(key);
  if (/^Key[A-Z]$/.test(code) && (!latin || e.altKey)) key = code.slice(3).toLowerCase();
  else if (/^Digit[0-9]$/.test(code) && (!latin || e.altKey) && !e.shiftKey) key = code.slice(5);
  const symbol = key.length === 1 && !/[a-z0-9]/.test(key);
  const mods: string[] = [];
  if (mac ? e.metaKey : e.ctrlKey) mods.push("mod");
  if (mac && e.ctrlKey) mods.push("ctrl");
  if (e.altKey) mods.push("alt");
  if (e.shiftKey && !symbol) mods.push("shift");
  return [...mods, key].join("+");
}

export function isMac(platform: string = typeof navigator !== "undefined" ? navigator.platform || navigator.userAgent : ""): boolean {
  return /mac|iphone|ipad|ipod/i.test(platform);
}

const MAC_SYMBOLS: Record<string, string> = { mod: "⌘", ctrl: "⌃", alt: "⌥", shift: "⇧" };
const PC_NAMES: Record<string, string> = { mod: "Ctrl", ctrl: "Ctrl", alt: "Alt", shift: "Shift" };
const KEY_NAMES: Record<string, string> = {
  arrowleft: "←",
  arrowright: "→",
  arrowup: "↑",
  arrowdown: "↓",
  escape: "Esc",
  delete: "Del",
  backspace: "⌫",
  space: "Space",
  enter: "Enter",
  pageup: "PgUp",
  pagedown: "PgDn",
  wheel: "Wheel",
};

/** The parts of a combo as shown on key caps: ["⌘", "⇧", "Z"] on a Mac, ["Ctrl", "Shift", "Z"] elsewhere. */
export function comboParts(combo: string, mac: boolean): string[] {
  const parts = normalizeCombo(combo).split("+");
  const key = parts.pop()!;
  const mods = parts.map((m) => (mac ? MAC_SYMBOLS[m] : PC_NAMES[m]) ?? m);
  return [...mods, KEY_NAMES[key] ?? (key.length === 1 ? key.toUpperCase() : key)];
}

export function formatCombo(combo: string, mac: boolean): string {
  return comboParts(combo, mac).join(mac ? "" : "+");
}

// --------------------------------------------------------------------------------------------- bindings
export type Overrides = Record<string, string[]>;
export type Bindings = Map<string, string[]>;

/** Default keys with the user's overrides applied (an override replaces all of an action's keys). */
export function resolveBindings(overrides: Overrides = {}, actions: ActionDef[] = ACTIONS): Bindings {
  return new Map(actions.map((a) => [a.id, (overrides[a.id] ?? a.keys).map(normalizeCombo)]));
}

function scopesOverlap(a: Scope, b: Scope): boolean {
  return a === b || a === "global" || b === "global";
}

export interface Conflict {
  combo: string;
  actions: [string, string];
}

/** Pairs of actions that share a combo in overlapping scopes. */
export function findConflicts(bindings: Bindings, actions: ActionDef[] = ACTIONS): Conflict[] {
  const byId = new Map(actions.map((a) => [a.id, a]));
  const seen = new Map<string, string[]>();
  const out: Conflict[] = [];
  for (const [id, combos] of bindings) {
    for (const combo of combos) {
      for (const other of seen.get(combo) ?? []) {
        if (scopesOverlap(byId.get(id)!.scope, byId.get(other)!.scope)) out.push({ combo, actions: [other, id] });
      }
      seen.set(combo, [...(seen.get(combo) ?? []), id]);
    }
  }
  return out;
}

/** Combos the browser or the OS keeps for itself: not offered for rebinding. */
export const RESERVED = new Set(["mod+w", "mod+t", "mod+n", "mod+q", "mod+r", "mod+l", "mod+tab", "mod+shift+t", "f5", "f11", "f12"]);

/** Why a rebinding was refused: `editor.hotkeys.rebind.<reason>` in the catalogs (`taken` names the other action). */
export type RebindRefusal = "unknown" | "modifierOnly" | "reserved" | "holdModifierWheel" | "pressKey" | "wheelAlone" | "taken";
export type RebindResult = { ok: true; overrides: Overrides } | { ok: false; reason: RebindRefusal; conflictWith?: string };

/** Binds `combo` to `actionId` (replacing its keys), unless it is reserved or another action in an overlapping
 * scope already uses it: the user then frees that one first (nothing is silently stolen). */
export function rebind(overrides: Overrides, actionId: string, combo: string, actions: ActionDef[] = ACTIONS): RebindResult {
  const action = actions.find((a) => a.id === actionId);
  if (!action) return { ok: false, reason: "unknown" };
  const normalized = normalizeCombo(combo);
  if (!normalized || normalized.endsWith("+") || ["mod", "ctrl", "alt", "shift"].includes(normalized.split("+").pop()!))
    return { ok: false, reason: "modifierOnly" };
  if (RESERVED.has(normalized)) return { ok: false, reason: "reserved" };
  const wheelAction = action.keys.some((k) => isWheelCombo(k));
  if (wheelAction !== isWheelCombo(normalized)) return { ok: false, reason: wheelAction ? "holdModifierWheel" : "pressKey" };
  if (normalized === "wheel") return { ok: false, reason: "wheelAlone" };
  const bindings = resolveBindings(overrides, actions);
  for (const [id, combos] of bindings) {
    if (id === actionId || !combos.includes(normalized)) continue;
    const other = actions.find((a) => a.id === id)!;
    if (scopesOverlap(other.scope, action.scope)) return { ok: false, reason: "taken", conflictWith: id };
  }
  return { ok: true, overrides: { ...overrides, [actionId]: [normalized] } };
}

export function resetBinding(overrides: Overrides, actionId: string): Overrides {
  const next = { ...overrides };
  delete next[actionId];
  return next;
}

// ----------------------------------------------------------------------------------------------- matching
export interface TargetLike {
  tagName?: string;
  isContentEditable?: boolean;
  type?: string;
}

const TEXT_INPUTS = new Set(["", "text", "search", "email", "number", "password", "tel", "url", "date", "time"]);

/** Whether the key goes to a field the user is typing in. */
export function isTypingTarget(target: TargetLike | null | undefined): boolean {
  if (!target) return false;
  if (target.isContentEditable) return true;
  const tag = (target.tagName || "").toLowerCase();
  if (tag === "textarea" || tag === "select") return true;
  return tag === "input" && TEXT_INPUTS.has((target.type || "").toLowerCase());
}

export interface MatchContext {
  scope: Exclude<Scope, "global">;
  mac: boolean;
  typing: boolean;
  repeat: boolean;
}

export const isWheelCombo = (combo: string) => normalizeCombo(combo).split("+").pop() === "wheel";

/** The combo a wheel event stands for ("shift+wheel"; ⌘ is `mod` on a Mac, Ctrl elsewhere). */
export function wheelCombo(e: Pick<KeyLike, "metaKey" | "ctrlKey" | "altKey" | "shiftKey">, mac: boolean): string {
  const mods: string[] = [];
  if (mac ? e.metaKey : e.ctrlKey) mods.push("mod");
  if (mac && e.ctrlKey) mods.push("ctrl");
  if (e.altKey) mods.push("alt");
  if (e.shiftKey) mods.push("shift");
  return [...mods, "wheel"].join("+");
}

/** The wheel gesture (an action of `scope`) a wheel event with these modifiers triggers, if any. */
export function wheelAction(e: Pick<KeyLike, "metaKey" | "ctrlKey" | "altKey" | "shiftKey">, bindings: Bindings, mac: boolean, scope: Scope = "sketch", actions: ActionDef[] = ACTIONS): string | null {
  const combo = wheelCombo(e, mac);
  if (combo === "wheel") return null;
  for (const a of actions) if (a.scope === scope && bindings.get(a.id)?.includes(combo)) return a.id;
  return null;
}

/** The action a key event triggers in this context, if any. */
export function matchAction(e: KeyLike, bindings: Bindings, ctx: MatchContext, actions: ActionDef[] = ACTIONS): ActionDef | null {
  const combo = eventCombo(e, ctx.mac);
  for (const a of actions) {
    if (a.scope !== "global" && a.scope !== ctx.scope) continue;
    if (ctx.typing && !a.inInputs) continue;
    if (ctx.repeat && !a.repeat) continue;
    if (bindings.get(a.id)?.includes(combo)) return a;
  }
  return null;
}

// ------------------------------------------------------------------------------------------------ storage
const STORAGE_KEY = "solaros.editor.keys";

export function loadOverrides(): Overrides {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : {};
    if (!parsed || typeof parsed !== "object") return {};
    const out: Overrides = {};
    for (const [id, keys] of Object.entries(parsed as Record<string, unknown>))
      if (ACTION_BY_ID.has(id) && Array.isArray(keys) && keys.every((k) => typeof k === "string")) out[id] = keys as string[];
    return out;
  } catch {
    return {};
  }
}

export function saveOverrides(overrides: Overrides): void {
  try {
    if (Object.keys(overrides).length) localStorage.setItem(STORAGE_KEY, JSON.stringify(overrides));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* private mode */
  }
}

/** The first key of an action, for tooltips ("Select (V)"). */
export function shortcutLabel(bindings: Bindings, actionId: string, mac: boolean): string | null {
  const combo = bindings.get(actionId)?.[0];
  return combo ? formatCombo(combo, mac) : null;
}
